import { coopActionWord, isNoActionLike } from './verdictDisplay';
import type { EnforcementAction, ModerationIntelligence, RuleEvaluation } from '../types';

export type RejectAttributionSource = 'enforcement' | 'preview' | 'rule' | 'verdict_reason';

export interface RejectAttribution {
  ruleName: string | null;
  reason: string | null;
  policies: string[];
  source: RejectAttributionSource;
}

/** Same precedence as the Coop scan badge: verdict, then action. */
function outcomeWord(row: EnforcementAction | undefined): string | null {
  if (!row) return null;
  return coopActionWord(row.verdict) ?? coopActionWord(row.action);
}

/**
 * Live case signal only. Matched TipMi rules are content-graph preview and must
 * not count as a reject when the verdict is approve, escalate, or no-action.
 */
function isLiveRejectSignal(intelligence: ModerationIntelligence): boolean {
  if (isNoActionLike(intelligence.verdict)) return false;
  const word = coopActionWord(intelligence.verdict);
  if (word === 'Rejected') return true;
  if (word != null) return false;
  return (intelligence.case_status ?? '').toLowerCase() === 'rejected';
}

function isRejectRule(rule: RuleEvaluation): boolean {
  if (!rule.matched) return false;
  const type = (rule.action_type ?? '').toUpperCase();
  const name = (rule.action_name ?? '').toUpperCase();
  return (
    type.includes('AUTO_REJECT') ||
    type.includes('REJECT') ||
    type.includes('HIDE') ||
    name.includes('AUTO_REJECT') ||
    name.includes('REJECT') ||
    name.includes('HIDE')
  );
}

function fromEnforcementRow(
  row: EnforcementAction,
  source: 'enforcement' | 'preview',
): RejectAttribution {
  return {
    ruleName: row.actor_id?.trim() || null,
    reason: row.reason?.trim() || null,
    policies: [],
    source,
  };
}

function fromMatchedRule(rule: RuleEvaluation): RejectAttribution {
  return {
    ruleName: rule.rule_name?.trim() || null,
    reason: rule.match_reason?.trim() || null,
    policies: [...(rule.linked_policies ?? [])].filter((p) => p.trim().length > 0),
    source: 'rule',
  };
}

/**
 * Best-effort reject attribution for the current live Coop outcome.
 * Uses the last enforcement row, else the last preview, else a reject verdict
 * (matched TipMi rule, then verdict_reason). Older reject rows and rule
 * previews do not override a later approve, escalate, or no-action outcome.
 */
export function pickRejectAttribution(
  intelligence: ModerationIntelligence | null | undefined,
): RejectAttribution | null {
  if (!intelligence) return null;

  const actions = intelligence.enforcement_actions ?? [];
  if (actions.length > 0) {
    const row = actions[actions.length - 1];
    if (!row || outcomeWord(row) !== 'Rejected') return null;
    return fromEnforcementRow(row, 'enforcement');
  }

  const previews = intelligence.enforcement_previews ?? [];
  if (previews.length > 0) {
    const row = previews[previews.length - 1];
    if (!row || outcomeWord(row) !== 'Rejected') return null;
    return fromEnforcementRow(row, 'preview');
  }

  if (!isLiveRejectSignal(intelligence)) return null;

  const rejectRules = (intelligence.rule_evaluations ?? []).filter(isRejectRule);
  if (rejectRules.length > 0) {
    return fromMatchedRule(rejectRules[rejectRules.length - 1]!);
  }

  const reason = intelligence.verdict_reason?.trim() || null;
  if (!reason) return null;
  return {
    ruleName: null,
    reason,
    policies: [],
    source: 'verdict_reason',
  };
}

/**
 * Content-overview banner. Mid-flight REJECT stays `Coop · Scanning` until the
 * deep scan settles or case_status is a strong terminal — never show a
 * confident Rejected banner beside that badge.
 */
export function rejectAttributionForScanLabel(
  scanLabel: string,
  intelligence: ModerationIntelligence | null | undefined,
): RejectAttribution | null {
  if (scanLabel !== 'Coop · Rejected') return null;
  return pickRejectAttribution(intelligence);
}

/** True when TipMi rule preview has a matched AUTO_REJECT / HIDE rule. */
export function hasMatchedRejectRule(
  intelligence: ModerationIntelligence | null | undefined,
): boolean {
  return (intelligence?.rule_evaluations ?? []).some(isRejectRule);
}
